/**
 * FilterPanel Component
 *
 * Desktop sidebar with all search filters.
 */

import React from 'react';
import { PriceRangeSlider, CheckboxGroup, Toggle, ButtonGroup } from '../../../components/ui';
import type { SearchFilters } from '../hooks/useSearchFilters';

// Filter options data - counts from actual database
const PROPERTY_TYPES = [
  { value: 'chalet', label: 'Chalet', count: 30 },
  { value: 'apartment', label: 'Apartment', count: 26 },
  { value: 'house', label: 'House', count: 17 },
  { value: 'cabin', label: 'Cabin', count: 10 },
  { value: 'villa', label: 'Villa', count: 6 },
  { value: 'caravan', label: 'Caravan', count: 4 },
  { value: 'studio', label: 'Studio', count: 3 },
  { value: 'bed-breakfast', label: 'B&B', count: 3 },
  { value: 'condo', label: 'Condo', count: 1 },
];

const AMENITIES = [
  { value: 'pool', label: 'Pool', count: 52 },
  { value: 'wifi', label: 'WiFi', count: 180 },
  { value: 'kitchen', label: 'Kitchen', count: 165 },
  { value: 'ac', label: 'Air conditioning', count: 142 },
  { value: 'parking', label: 'Free parking', count: 98 },
  { value: 'beach_access', label: 'Beach access', count: 28 },
  { value: 'gym', label: 'Gym', count: 15 },
  { value: 'fireplace', label: 'Fireplace', count: 35 },
  { value: 'washer', label: 'Washer', count: 89 },
  { value: 'dryer', label: 'Dryer', count: 45 },
  { value: 'hot_tub', label: 'Hot tub', count: 12 },
  { value: 'bbq', label: 'BBQ grill', count: 65 },
];

const BEDROOM_OPTIONS = ['Any', '1', '2', '3', '4', '5+'];
const BATHROOM_OPTIONS = ['Any', '1', '2', '3+'];

interface FilterPanelProps {
  filters: SearchFilters;
  onChange: (filters: Partial<SearchFilters>) => void;
  onClear: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onChange,
  onClear,
}) => {
  return (
    <aside className="w-64 shrink-0 space-y-6 pr-6 border-r border-gray-200">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Filters</h2>
        <button
          type="button"
          onClick={onClear}
          className="text-sm text-primary-600 font-medium hover:text-primary-700"
        >
          Clear all
        </button>
      </div>

      {/* Price Range */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Price per night</h3>
        <PriceRangeSlider
          min={0}
          max={1000}
          value={[filters.priceMin || 0, filters.priceMax || 1000]}
          onChange={([min, max]) =>
            onChange({
              priceMin: min === 0 ? undefined : min,
              priceMax: max === 1000 ? undefined : max,
            })
          }
        />
      </section>

      {/* Property Type */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Property type</h3>
        <CheckboxGroup
          options={PROPERTY_TYPES}
          value={filters.propertyTypes}
          onChange={(propertyTypes) => onChange({ propertyTypes })}
          maxVisible={5}
        />
      </section>

      {/* Bedrooms */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Bedrooms</h3>
        <ButtonGroup
          options={BEDROOM_OPTIONS}
          value={
            filters.bedrooms
              ? filters.bedrooms >= 5
                ? '5+'
                : String(filters.bedrooms)
              : 'Any'
          }
          onChange={(val) =>
            onChange({
              bedrooms:
                val === 'Any'
                  ? undefined
                  : val === '5+'
                  ? 5
                  : parseInt(val, 10),
            })
          }
          size="sm"
        />
      </section>

      {/* Bathrooms */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Bathrooms</h3>
        <ButtonGroup
          options={BATHROOM_OPTIONS}
          value={
            filters.bathrooms
              ? filters.bathrooms >= 3
                ? '3+'
                : String(filters.bathrooms)
              : 'Any'
          }
          onChange={(val) =>
            onChange({
              bathrooms:
                val === 'Any'
                  ? undefined
                  : val === '3+'
                  ? 3
                  : parseInt(val, 10),
            })
          }
          size="sm"
        />
      </section>

      {/* Amenities */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Amenities</h3>
        <CheckboxGroup
          options={AMENITIES}
          value={filters.amenities}
          onChange={(amenities) => onChange({ amenities })}
          maxVisible={6}
        />
      </section>

      {/* Booking Options */}
      <section>
        <h3 className="font-medium text-gray-900 mb-3">Booking options</h3>
        <div className="space-y-2">
          <Toggle
            label="Instant Book"
            description="Book without waiting for host approval"
            checked={filters.instantBooking || false}
            onChange={(instantBooking) => onChange({ instantBooking })}
          />
          <Toggle
            label="Pet friendly"
            description="Allows pets on the property"
            checked={filters.petFriendly || false}
            onChange={(petFriendly) => onChange({ petFriendly })}
          />
        </div>
      </section>
    </aside>
  );
};

export default FilterPanel;
